import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wind, Settings, Wrench, Repeat, Phone, Mail, MapPin, Clock, Star, CheckCircle, ArrowRight, Menu, X, MessageCircle, ClipboardList, HardHat, Cog, ShieldCheck, FileSearch, Calendar, Sparkles, Activity, Users, Factory, Zap, Flame, Leaf, Award, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { companyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  wind: Wind,
  settings: Settings,
  wrench: Wrench,
  repeat: Repeat,
  hardhat: HardHat,
  users: Users,
  cog: Cog
};

const Home = () => {
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  // Fetch services and testimonials on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/testimonials`)
        ]);
        setServices(servicesRes.data);
        setTestimonials(testimonialsRes.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: "Error",
          description: "Failed to load data. Please refresh the page.",
          variant: "destructive"
        });
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/quotes`, formData);
      if (response.data.success) {
        toast({
          title: "Quote Request Sent!",
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast({
        title: "Error",
        description: "Failed to submit quote request. Please try again.",
        variant: "destructive"
      });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                  <Wind className="h-7 w-7 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-cyan-400 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-cyan-700 bg-clip-text text-transparent">
                    Sky
                  </span>
                  <span className="text-2xl font-bold text-cyan-600">Control</span>
                </div>
                <div className="flex items-center gap-1 -mt-1">
                  <span className="text-xs font-medium text-slate-600 tracking-wide">SOLUTION & TRADING</span>
                  <div className="w-1 h-1 bg-cyan-500 rounded-full"></div>
                  <span className="text-xs font-medium text-slate-500">PTE LTD</span>
                </div>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('services')} className="text-slate-700 hover:text-cyan-600 transition-colors">Services</button>
              <button onClick={() => scrollToSection('process')} className="text-slate-700 hover:text-cyan-600 transition-colors">Process</button>
              <button onClick={() => scrollToSection('clients')} className="text-slate-700 hover:text-cyan-600 transition-colors">Clients</button>
              <Link to="/profile" data-testid="nav-profile-link" className="text-slate-700 hover:text-cyan-600 transition-colors">Profile</Link>
              <a href="https://wa.me/6581130354" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:text-green-700 transition-colors">
                <MessageCircle className="h-5 w-5" />
                <span>WhatsApp</span>
              </a>
              <Button onClick={() => scrollToSection('contact')} className="bg-cyan-600 hover:bg-cyan-700 text-white">Get Quote</Button>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col gap-3">
              <button onClick={() => scrollToSection('services')} className="text-slate-700 hover:text-cyan-600 transition-colors text-left">Services</button>
              <button onClick={() => scrollToSection('process')} className="text-slate-700 hover:text-cyan-600 transition-colors text-left">Process</button>
              <button onClick={() => scrollToSection('clients')} className="text-slate-700 hover:text-cyan-600 transition-colors text-left">Clients</button>
              <Link to="/profile" data-testid="mobile-nav-profile-link" className="text-slate-700 hover:text-cyan-600 transition-colors text-left">Profile</Link>
              <a href="https://wa.me/6581130354" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:text-green-700 transition-colors">
                <MessageCircle className="h-5 w-5" />
                <span>WhatsApp Us</span>
              </a>
              <Button onClick={() => scrollToSection('contact')} className="bg-cyan-600 hover:bg-cyan-700 text-white w-full">Get Quote</Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Integrated <span className="text-cyan-600">Industrial Services</span> Partner
          </h2>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            Sky Control Solution & Trading Pte Ltd is a Singapore-based provider of process construction, maintenance, and manpower supply solutions for the energy, utilities, and infrastructure sectors.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => scrollToSection('contact')} size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white text-lg px-8 py-6" data-testid="hero-quote-btn">
              Request a Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Link to="/profile" data-testid="hero-profile-btn">
              <Button size="lg" variant="outline" className="border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 text-lg px-8 py-6 w-full">
                View Company Profile
              </Button>
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-600">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-cyan-600" />
              <span>Certified Workforce</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-cyan-600" />
              <span>Safety-First Culture</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-cyan-600" />
              <span>Trusted by Major Industry Players</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-xl text-slate-600">Integrated engineering support for industrial plants and facilities</p>
          </div>
          {loading ? (
            <div className="text-center text-slate-600">Loading services...</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => {
                const Icon = iconMap[service.icon] || Cog;
                return (
                  <Card key={service.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-none">
                    <CardHeader>
                      <div className="w-14 h-14 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="h-7 w-7 text-cyan-600" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                      <CardDescription className="text-slate-600 leading-relaxed">{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle className="h-4 w-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Our Work Process Section */}
      <section id="process" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Our Work Process</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              A disciplined workflow covering every stage of process construction and maintenance — engineered for safety, quality, and on-time delivery on industrial sites.
            </p>
          </div>

          {/* Construction Process */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <HardHat className="h-6 w-6 text-cyan-600" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900">Process Construction Workflow</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: '01',
                  icon: FileSearch,
                  title: 'Site Survey & Engineering',
                  desc: 'On-site assessment of scope, layout, utilities and constraints. Engineering review aligned to client specs and local regulations.'
                },
                {
                  step: '02',
                  icon: ClipboardList,
                  title: 'Planning & Quotation',
                  desc: 'Detailed method statement, material take-off, manpower plan, HSE plan, and a transparent itemised quotation with milestones.'
                },
                {
                  step: '03',
                  icon: Cog,
                  title: 'Piping, Steelwork & Assembly',
                  desc: 'Certified welders, pipefitters and riggers execute piping, structural steel, equipment installation and system integration on site.'
                },
                {
                  step: '04',
                  icon: ShieldCheck,
                  title: 'Testing & Commissioning',
                  desc: 'Pressure tests, inspection, instrumentation loop check, performance testing and formal handover with full documentation.'
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <Card key={idx} className="relative border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                          <Icon className="h-6 w-6 text-cyan-600" />
                        </div>
                        <span className="text-3xl font-bold text-cyan-200">{item.step}</span>
                      </div>
                      <h4 className="font-bold text-lg mb-2 text-slate-900">{item.title}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Maintenance Process */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Settings className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900">Maintenance Workflow</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: '01',
                  icon: Calendar,
                  title: 'Maintenance Contract',
                  desc: 'Define scope, frequency, KPIs, and on-call response. Preventive, predictive, or corrective — tailored to your asset criticality.'
                },
                {
                  step: '02',
                  icon: Activity,
                  title: 'Inspection & Diagnostics',
                  desc: 'Routine inspection of mechanical, electrical, and instrumentation systems using advanced diagnostic tools and condition monitoring.'
                },
                {
                  step: '03',
                  icon: Sparkles,
                  title: 'Servicing & Repair',
                  desc: 'Planned servicing or rapid corrective action by trained crews to minimise downtime and protect equipment longevity.'
                },
                {
                  step: '04',
                  icon: ClipboardList,
                  title: 'Reporting & Improvement',
                  desc: 'Service reports with findings, root-cause analysis and recommendations for continuous reliability improvement.'
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <Card key={idx} className="relative border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Icon className="h-6 w-6 text-blue-600" />
                        </div>
                        <span className="text-3xl font-bold text-blue-200">{item.step}</span>
                      </div>
                      <h4 className="font-bold text-lg mb-2 text-slate-900">{item.title}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Company Profile</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Sky Control Solution & Trading Pte Ltd is a premier provider of <span className="font-semibold text-slate-800">integrated industrial services</span>, specialising in process construction, maintenance, and manpower supply solutions.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Based in Singapore, we deliver high-quality engineering support to critical sectors including <span className="font-semibold text-slate-800">energy, utilities, and infrastructure</span>. Our commitment to safety, reliability, and operational excellence makes us a trusted partner for major industry players.
              </p>
              <div className="mb-6">
                <h3 className="font-bold text-slate-900 mb-3">Our Field of Work</h3>
                <ul className="grid grid-cols-1 gap-2 text-slate-700">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-600 flex-shrink-0" /> Industrial plants & facilities</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-600 flex-shrink-0" /> District cooling & energy recovery facilities</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-600 flex-shrink-0" /> Gas distribution system maintenance</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-600 flex-shrink-0" /> Electrical infrastructure & system upgrades</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-cyan-600 flex-shrink-0" /> Plant expansion & modification works</li>
                </ul>
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/profile" data-testid="about-view-profile-btn">
                  <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">View Full Company Profile <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </Link>
              </div>
            </div>
            <div className="space-y-6">
              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Safety & Compliance</h3>
                      <p className="text-slate-600">We uphold the highest standards of safety and environmental stewardship — every project managed with strong risk mitigation and full compliance with local regulations.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Award className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Certified Workforce</h3>
                      <p className="text-slate-600">Certified welders, pipefitters, electricians, instrument technicians and supervisors — all rigorously vetted and safety-trained to industry requirements.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Operational Excellence</h3>
                      <p className="text-slate-600">We deliver value through innovation, efficiency, and unwavering customer service — keeping critical assets reliable and productive.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Clients Section */}
      <section id="clients" className="py-20 px-4 bg-slate-900 text-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Key Clients & Partnerships</h2>
            <p className="text-xl text-slate-300">Trusted by leading names across energy, utilities, and infrastructure</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Veolia Energy Pte Ltd', sector: 'Energy & District Cooling', icon: Leaf },
              { name: 'City Gas', sector: 'Gas Distribution', icon: Flame },
              { name: 'Brightsun Electrical Pte Ltd', sector: 'Electrical Infrastructure', icon: Zap },
              { name: 'Lexington Enterprises Pte Ltd', sector: 'Industrial Projects', icon: Factory }
            ].map((client, idx) => {
              const Icon = client.icon;
              return (
                <div key={idx} data-testid={`client-card-${idx}`} className="bg-slate-800/60 border border-slate-700 rounded-xl p-6 hover:border-cyan-500 hover:bg-slate-800 transition-all">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-cyan-400" />
                  </div>
                  <h3 className="font-bold text-lg mb-1">{client.name}</h3>
                  <p className="text-sm text-slate-400">{client.sector}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-slate-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-slate-600">Don't just take our word for it</p>
          </div>
          {loading ? (
            <div className="text-center text-slate-600">Loading testimonials...</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.comment}"</p>
                    <div className="border-t pt-4">
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-600">{testimonial.company}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-slate-600">Request a free quote or reach out for any inquiries</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-none shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Request a Quote</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input 
                      name="name"
                      placeholder="Your Name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Input 
                      name="email"
                      type="email"
                      placeholder="Email Address" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Input 
                      name="phone"
                      placeholder="Phone Number" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <select 
                      name="service"
                      className="w-full px-3 py-2 border border-input bg-background rounded-md"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Service</option>
                      <option value="process-construction">Process Construction</option>
                      <option value="maintenance">Maintenance Services</option>
                      <option value="manpower">Manpower Supply Solutions</option>
                      <option value="other">Other / General Enquiry</option>
                    </select>
                  </div>
                  <div>
                    <Textarea 
                      name="message"
                      placeholder="Additional Details" 
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg">
                    Send Quote Request
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Phone</h3>
                      <p className="text-slate-700">{companyInfo.phone}</p>
                      <p className="text-sm text-slate-600 mt-1">{companyInfo.emergencyHours}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">WhatsApp</h3>
                      <a 
                        href="https://wa.me/6581130354" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 font-medium"
                      >
                        Chat with us on WhatsApp
                      </a>
                      <p className="text-sm text-slate-600 mt-1">Quick response via WhatsApp</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Email</h3>
                      <p className="text-slate-700">{companyInfo.email}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Address</h3>
                      <p className="text-slate-700">{companyInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">Business Hours</h3>
                      <p className="text-slate-700">{companyInfo.hours}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center shadow-lg">
                    <Wind className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-cyan-300 rounded-full flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-bold text-white">Sky</span>
                    <span className="text-lg font-bold text-cyan-400">Control</span>
                  </div>
                  <div className="flex items-center gap-1 -mt-1">
                    <span className="text-xs font-medium text-slate-300 tracking-wide">SOLUTION & TRADING</span>
                  </div>
                </div>
              </div>
              <p className="text-slate-400 text-sm">{companyInfo.tagline}</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Process Construction</li>
                <li>Maintenance Services</li>
                <li>Manpower Supply</li>
                <li>Engineering Support</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>About Us</li>
                <li>Our Team</li>
                <li>Testimonials</li>
                <li>Contact</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>{companyInfo.phone}</li>
                <li>{companyInfo.email}</li>
                <li className="pt-2">{companyInfo.address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>© 2024 Sky Control Solution & Trading Pte Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;